/**
 * Mobs and NPCs — everything in the world that is not the player.
 *
 * An actor is a `CharacterController` (the same locomotion the player runs,
 * driven by intent rather than input) plus, when it is close enough to matter,
 * a body to draw it with.
 *
 * Bodies are pooled. A dozen mobs at three meshes and seven pipelines each is
 * not a thing this renderer would forgive, so mobs are drawn "lite" — no
 * garment solve, no fur — and only the nearest few hold a body at all. The rest
 * keep simulating; they simply are not drawn, which at sixty metres through
 * this much aerial perspective is not a distinction the eye can make.
 */

import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { CharacterController } from "../character/controller.js";
import { Character } from "../character/character.js";
import { SnowContact } from "../character/snowContact.js";
import { MOBS, SPAWNS, NPCS } from "./worldData.js";
import { S } from "../core/settings.js";

/** How many mob bodies exist. Beyond this, mobs simulate without being drawn. */
const MOB_BODY_POOL = 8;
/** How often the pool is re-sorted, seconds. */
const LOD_INTERVAL = 0.4;

/** Base body height in metres, from the figure's bind pose. */
const FIGURE_HEIGHT = 1.79;

let actorUid = 0;

class Actor {
    /**
     * @param {import("../terrain/terrain.js").Terrain} terrain
     * @param {{x:number,z:number}} pos
     */
    constructor(terrain, pos) {
        this.id = `actor_${actorUid++}`;
        this.controller = new CharacterController(terrain);
        this.controller.position.set(pos.x, terrain.heightAt(pos.x, pos.z), pos.z);
        this.controller.facing = Math.random() * Math.PI * 2;
        /** @type {Character|null} */
        this.render = null;
        /** @type {SnowContact|null} */
        this.contact = null;
        this.scale = 1;
    }

    get position() {
        return this.controller.position;
    }

    /** Capsule for click picking, scaled with the body. */
    get pickRadius() {
        return 0.45 * this.scale + 0.12;
    }

    get pickHeight() {
        return FIGURE_HEIGHT * this.scale;
    }

    /** Where a name plate or a damage number should sit. */
    get headY() {
        return this.position.y + this.pickHeight + 0.35;
    }
}

export class MobActor extends Actor {
    /**
     * @param {import("../terrain/terrain.js").Terrain} terrain
     * @param {string} mobId
     * @param {{x:number,z:number}} pos
     */
    constructor(terrain, mobId, pos) {
        super(terrain, pos);
        const data = MOBS[mobId];
        this.kind = "mob";
        this.mobId = mobId;
        this.data = data;
        this.name = data.name;
        this.level = data.level;
        this.type = data.type;
        this.maxHp = data.maxHp;
        this.hp = data.maxHp;
        this.scale = data.scale;

        this.spawn = new Vector3(pos.x, this.position.y, pos.z);

        // ---- combat / AI state, owned here, written by `combat.js` ----------
        this.isDead = false;
        this.respawnIn = 0;
        this.aggro = false;
        this.state = "idle";
        this.attackCooldown = 0;
        this.wanderIn = 2 + Math.random() * 6;
        /** @type {{x:number,z:number}|null} */
        this.wanderTo = null;
        /** @type {{id:string, until:number, mods:object}[]} */
        this.debuffs = [];
    }

    get moveSpeed() {
        let speed = this.data.moveSpeed;
        for (const d of this.debuffs) if (d.mods.moveSpeed) speed += d.mods.moveSpeed;
        return Math.max(0.6, speed);
    }

    /** Back to full, at the spawn point, forgetting everything. */
    reset() {
        this.hp = this.maxHp;
        this.isDead = false;
        this.aggro = false;
        this.state = "idle";
        this.debuffs.length = 0;
        this.controller.position.copyFrom(this.spawn);
        this.controller.velocity.set(0, 0, 0);
        this.render?.rebind(this.controller);
    }
}

export class NpcActor extends Actor {
    constructor(terrain, def) {
        super(terrain, def.position);
        this.kind = "npc";
        this.def = def;
        this.name = def.name;
        this.title = def.title;
        this.isDead = false;
        this.scale = 1;
        /** Whether the body is currently being solved and drawn. */
        this.drawn = true;
    }
}

/**
 * Owns every actor, the body pool, and the per-frame simulation of both.
 */
export class ActorSystem {
    /**
     * @param {{
     *   scene: import("@babylonjs/core/scene").Scene,
     *   terrain: import("../terrain/terrain.js").Terrain,
     *   sky: import("../render/sky.js").Sky,
     *   shadows: import("../render/shadows.js").ShadowSystem,
     *   spray: import("../vfx/particles.js").SprayField,
     *   depthPass: import("../render/depthPass.js").DepthPass,
     *   player: import("../character/controller.js").CharacterController,
     * }} ctx
     */
    constructor(ctx) {
        this.ctx = ctx;
        const { terrain } = ctx;

        /** @type {MobActor[]} */
        this.mobs = SPAWNS.map((s) => new MobActor(terrain, s.mob, s));
        /** @type {NpcActor[]} */
        this.npcs = NPCS.map((def) => new NpcActor(terrain, def));

        /** Everything clickable, in one array so the picker does one pass. */
        this.all = [...this.mobs, ...this.npcs];

        /** @type {Character[]} */
        this.pool = [];
        this._lodTimer = 0;

        this._buildBodies();
    }

    _buildBodies() {
        const { terrain, spray, depthPass } = this.ctx;

        // NPCs stand where the player spawns and never move; they get their own
        // bodies rather than competing for pool slots.
        for (const npc of this.npcs) {
            npc.render = this._makeBody(npc.controller, NPC_PALETTE, 1);
            npc.contact = new SnowContact(npc.controller, terrain.deform, npc.render.figure, spray);
        }

        for (let i = 0; i < MOB_BODY_POOL; i++) {
            const body = this._makeBody(this.mobs[0].controller, null, 1);
            body.setVisible(false);
            body._owner = null;
            // One contact writer per body, rebound on hand-off, so the pool
            // allocates nothing once the world is up.
            body._contact = new SnowContact(
                this.mobs[0].controller, terrain.deform, body.figure, spray
            );
            this.pool.push(body);
        }

        if (depthPass) {
            for (const npc of this.npcs) npc.render.registerPrepass(depthPass);
            for (const body of this.pool) body.registerPrepass(depthPass);
        }
    }

    _makeBody(controller, palette, scale) {
        const { scene, terrain, sky, shadows } = this.ctx;
        return new Character(scene, terrain, sky, shadows, controller, {
            cloth: false,
            fur: false,
            cascades: 2,
            palette,
            scale,
        });
    }

    /** Compile every actor pipeline behind the loading screen. */
    async warmUp() {
        for (const npc of this.npcs) {
            npc.render.update(0);
            await npc.render.warmUp();
        }
        for (const body of this.pool) {
            body.update(0);
            await body.warmUp();
        }
    }

    /**
     * Push per-frame uniforms, for the bodies that are actually drawn. An
     * unowned pool body is invisible; pushing forty uniforms at it every frame
     * would be forty uniforms for nothing.
     * @param {Vector3} cameraPos
     */
    sync(cameraPos) {
        for (const npc of this.npcs) if (npc.drawn) npc.render.sync(cameraPos);
        for (const body of this.pool) if (body._owner) body.sync(cameraPos);
    }

    /**
     * Integrate every actor, then pose and stamp the ones that are drawn.
     * Runs before `terrain.update()`, so the footprints land in this frame's
     * brush list.
     * @param {number} dt
     */
    update(dt) {
        const p = this.ctx.player.position;

        for (const npc of this.npcs) {
            npc.controller.update(dt);
            // The townsfolk hold their own bodies, but a body being permanent is
            // not a reason to solve a skeleton for someone standing four hundred
            // metres away in the fog.
            const near = Math.hypot(npc.position.x - p.x, npc.position.z - p.z)
                < S.actorDrawRange;
            if (near !== npc.drawn) {
                npc.drawn = near;
                npc.render.setVisible(near);
            }
            if (!near) continue;
            npc.render.update(dt);
            npc.contact.update(dt);
        }

        for (const mob of this.mobs) {
            if (mob.isDead) continue;
            mob.controller.update(dt);
        }

        this._lodTimer -= dt;
        if (this._lodTimer <= 0) {
            this._lodTimer = LOD_INTERVAL;
            this._assignBodies();
        }

        for (const mob of this.mobs) {
            if (!mob.render) continue;
            mob.render.update(dt);
            mob.contact?.update(dt);
        }
    }

    /**
     * Hand the pool to the nearest living mobs.
     *
     * Sorting fifteen actors by distance twice a second is nothing; what this
     * really buys is that a mob that walks out of range gives its body up
     * instead of keeping one warm for an empty patch of snow.
     */
    _assignBodies() {
        const p = this.ctx.player.position;
        const candidates = this.mobs
            .filter((m) => !m.isDead)
            .map((m) => ({
                mob: m,
                d: Math.hypot(m.position.x - p.x, m.position.z - p.z),
            }))
            .filter((c) => c.d < S.actorDrawRange)
            .sort((a, b) => a.d - b.d)
            .slice(0, this.pool.length)
            .map((c) => c.mob);

        const keep = new Set(candidates);
        for (const mob of this.mobs) {
            if (mob.render && !keep.has(mob)) this._release(mob);
        }

        for (const mob of candidates) {
            if (mob.render) continue;
            const free = this.pool.find((b) => !b._owner);
            if (!free) break;
            this._attach(mob, free);
        }
    }

    _attach(mob, body) {
        body._owner = mob;
        body.scale = mob.scale;
        body.rebind(mob.controller);
        body.setVisible(true);
        body.tint(mob.data.visual.tint);
        body._contact.rebind(mob.controller, body.figure);
        mob.render = body;
        mob.contact = body._contact;
    }

    /** Give a mob's body back to the pool — on death, or on walking out of range. */
    release(mob) {
        this._release(mob);
    }

    _release(mob) {
        const body = mob.render;
        if (!body) return;
        body.setVisible(false);
        body._owner = null;
        mob.render = null;
        mob.contact = null;
    }

    /** Every actor that is alive and pickable. */
    pickables() {
        const list = [];
        for (const m of this.mobs) if (!m.isDead) list.push(m);
        for (const n of this.npcs) list.push(n);
        return list;
    }

    /** @param {string} id */
    byId(id) {
        return this.all.find((a) => a.id === id) ?? null;
    }
}

/** NPCs are townsfolk: warmer, lighter, obviously not a monster. */
const NPC_PALETTE = [
    [0.115, 0.075, 0.145, 0.80],
    [0.165, 0.115, 0.075, 0.74],
    [0.290, 0.265, 0.215, 0.82],
    [0.070, 0.050, 0.032, 0.60],
    [0.165, 0.120, 0.092, 0.85],
    [0.235, 0.185, 0.090, 0.70],
    null,
    null,
];
